import React, { useState } from "react";
import axios from "axios";
import { X, Leaf, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterPopup({ onClose }) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState(null);

  const handleDismiss = () => {
    localStorage.setItem("rasabox_newsletter_dismissed", new Date().toISOString());
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, source: "popup" });
      localStorage.setItem("rasabox_newsletter", "true");
      setIsDone(true);
      setTimeout(onClose, 3000);
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg === "already_subscribed") {
        localStorage.setItem("rasabox_newsletter", "true");
        setIsDone(true);
        setTimeout(onClose, 2500);
      } else {
        setError("Couldn't sign you up. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ backgroundColor: "rgba(45,52,54,0.45)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="w-full max-w-sm rounded-3xl overflow-hidden relative animate-fade-up"
        style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8", boxShadow: "0 24px 60px rgba(0,0,0,0.18)" }}
        data-testid="newsletter-popup"
      >
        {/* Top accent gradient */}
        <div className="h-1" style={{ background: "linear-gradient(90deg, #E07A5F, #F2CC8F, #81B29A)" }} />

        {/* Dismiss */}
        <button
          data-testid="newsletter-dismiss-btn"
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:bg-muted"
        >
          <X size={15} style={{ color: "#9AA5B1" }} />
        </button>

        <div className="px-7 py-6">
          {!isDone ? (
            <>
              {/* Icon */}
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: "#F2CC8F33", border: "1.5px solid #F2CC8F80" }}>
                <Leaf size={18} style={{ color: "#B8860B" }} strokeWidth={1.5} />
              </div>

              {/* Copy */}
              <h3 className="font-serif font-bold text-xl mb-2" style={{ color: "#2D3436" }}>
                Stay rooted in rhythm
              </h3>
              <p className="text-sm leading-relaxed mb-5" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
                One seasonal Ayurvedic recipe &amp; wellness tip every week — straight from{" "}
                <a href="https://thatayurvedaguy.com" target="_blank" rel="noopener noreferrer"
                  className="font-semibold" style={{ color: "#E07A5F" }}>
                  ThatAyurvedaGuy
                </a>.
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#9AA5B1" }} />
                  <input
                    data-testid="newsletter-email-input"
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    style={{
                      width: "100%",
                      padding: "11px 12px 11px 40px",
                      borderRadius: "12px",
                      border: "2px solid #E5E0D8",
                      backgroundColor: "#FDFBF7",
                      fontFamily: "Manrope, sans-serif",
                      fontSize: "14px",
                      color: "#2D3436",
                      outline: "none",
                    }}
                  />
                </div>

                {error && (
                  <p className="text-xs px-1" style={{ color: "#C0392B", fontFamily: "Manrope, sans-serif" }}>{error}</p>
                )}

                <button
                  data-testid="newsletter-subscribe-btn"
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] disabled:opacity-60"
                  style={{
                    backgroundColor: "#E07A5F",
                    color: "#FFFFFF",
                    fontFamily: "Manrope, sans-serif",
                    boxShadow: "0 6px 18px rgba(224,122,95,0.3)",
                  }}
                >
                  {isLoading ? "Signing you up..." : "Join the Community"}
                </button>
              </form>

              <button
                data-testid="newsletter-not-now-btn"
                onClick={handleDismiss}
                className="w-full text-center text-xs mt-3 py-1 transition-colors hover:opacity-70"
                style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}
              >
                Not now &nbsp;&middot;&nbsp; No spam, unsubscribe anytime
              </button>
            </>
          ) : (
            /* Success state */
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: "#81B29A20", border: "2px solid #81B29A50" }}>
                <Leaf size={22} style={{ color: "#81B29A" }} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif font-bold text-xl mb-2" style={{ color: "#2D3436" }}>
                You're in!
              </h3>
              <p className="text-sm" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
                Welcome to the RasaBox community. Ayurvedic wisdom coming your way.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
